import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface Subtask {
  id: string;
  title: string;
  status: string;
  priority: string | null;
  due_date: string | null;
}

export const useSubtasks = (parentTaskId: string | null) => {
  const { user, company } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ['subtasks', parentTaskId];

  const subtasksQuery = useQuery({
    queryKey,
    queryFn: async () => {
      if (!parentTaskId || !company?.id) return [];
      const { data, error } = await supabase
        .from('tasks')
        .select('id, title, status, priority, due_date')
        .eq('parent_task_id', parentTaskId)
        .eq('company_id', company.id)
        .is('deleted_at', null)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as Subtask[];
    },
    enabled: !!parentTaskId && !!company?.id,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const createMutation = useMutation({
    mutationFn: async (title: string) => {
      const userId = user?.id;
      const { data, error } = await supabase
        .from('tasks')
        .insert({
          title,
          parent_task_id: parentTaskId,
          company_id: company!.id,
          created_by: userId,
          assigned_to: userId,
          status: 'todo',
          is_private: false,
          is_system_generated: false,
        })
        .select('id')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: invalidate,
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, currentStatus }: { id: string; currentStatus: string }) => {
      const newStatus = currentStatus === 'completed' ? 'todo' : 'completed';
      const { error } = await supabase
        .from('tasks')
        .update({
          status: newStatus,
          completed_at: newStatus === 'completed' ? new Date().toISOString() : null,
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: async (subtaskId: string) => {
      const { error } = await supabase
        .from('tasks')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', subtaskId);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return {
    subtasks: subtasksQuery.data ?? [],
    isLoading: subtasksQuery.isLoading,
    createSubtask: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    toggleSubtask: toggleMutation.mutate,
    deleteSubtask: deleteMutation.mutate,
  };
};
